import {
  Container,
  Flex,
  Form,
  Grid,
  Link,
  LoadingIcon,
  Stack,
  Text,
} from "basis";
import { useAccounts } from "library/accounts";
import { useProducts } from "library/hooks/useProducts";
import { useCallback, useEffect, useMemo } from "react";
import { AccountsSelect } from "../AccountsSelect/AccountsSelect";
import { SvgInline } from "../SvgInline/SvgInline";
import { FORM_SUBMITTED } from "./constants";
import { DirectDebitLandingContent } from "./DirectDebitLandingContent";
import { AccountOptionType, ToAccountData } from "./types";

export const DirectDebitLanding = ({ state, dispatch }) => {
  const accountsInfo = useAccounts({ fullCreditCardsInfo: true });
  const productsInfo = useProducts();

  const accountOptions: AccountOptionType[] = useMemo(() => {
    if (!accountsInfo.data || !productsInfo.data) {
      return [];
    }

    return accountsInfo.data.credit_cards.reduce(
      (acc: AccountOptionType[], account) => {
        if (account.links.length === 0) {
          return acc;
        }

        const product = productsInfo.data?.find(
          (p) => p.id === account.product_id,
        );

        if (product?.region === "AU") {
          acc.push({
            data: {
              status: account.status,
              isOverdue: (account.credit_card?.amount_overdue ?? 0) > 0,
              cardName: product.name,
              cardEndsWith: account.credit_card?.linked_card_last4,
              cardToken: account.credit_card?.token,
              westpacUserId: product.westpac_direct_debit_id,
              prfi: product.features.prfi?.enabled === true,
            },
            value: account.account_id,
          });
        }

        return acc;
      },
      [],
    );
  }, [accountsInfo.data, productsInfo.data]);
  const initialValues = {
    toAccount:
      state.toAccount?.id ??
      (accountOptions.length === 1 ? accountOptions[0].value : ""),
  };
  const onSubmit = ({ errors }) => {
    if (Object.keys(errors).length === 0) {
      dispatch({
        type: FORM_SUBMITTED,
      });
    }
  };

  const dropDownChange = useCallback(
    ({
      selectedOption,
    }: {
      selectedOption: {
        value: string;
        data: ToAccountData;
      };
    }) => {
      dispatch({
        type: "ACCOUNT_SELECTED",
        toAccount: {
          id: selectedOption.value,
          data: selectedOption.data,
        },
      });
    },
    [dispatch],
  );

  useEffect(() => {
    /**
     * 🦘 If only one option is passed to the drop down, that option
     * will be preselected, therefore we need to populate the store with the option.
     */
    if ((accountOptions || []).length === 1) {
      dropDownChange({ selectedOption: accountOptions[0] });
    }
  }, [accountOptions, dropDownChange]);

  if (accountsInfo.isLoading) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  if (accountsInfo.isError) {
    return (
      <Text color="conditional.negative.text">
        <strong>{accountsInfo.error?.message}</strong>
      </Text>
    );
  }

  if (accountOptions.length === 0) {
    return (
      <Stack gap="8">
        <SvgInline name="direct-debit-payment-cancel" size="92" />
        <Text textStyle="heading5" textStyle-sm="heading4">
          You don't have any active accounts
        </Text>
        <Text>
          You'll need an active Latitude account to set up a direct debit
          request.
        </Text>
        <Link appearance="primary-button" href="/" newTab={false}>
          Go to overview
        </Link>
      </Stack>
    );
  }

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: formState }) => {
        const selectedAccountOption = accountOptions.find(
          (accountOption) => accountOption.value === formState.values.toAccount,
        )?.data;
        const isOverdue = selectedAccountOption?.isOverdue === true;
        const cardToken = selectedAccountOption?.cardToken;

        return (
          <>
            <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="2">
              <Grid.Item colSpan="all" colSpan-sm="0-1">
                <Text>
                  <strong>To account</strong>
                </Text>
              </Grid.Item>
              <Grid.Item colSpan="all" colSpan-sm="2-7">
                <Container bg="grey.t05" padding="4">
                  <AccountsSelect
                    name="toAccount"
                    label="To account"
                    hideLabel
                    accountOptions={accountOptions.filter(
                      (acc) => acc.data.status !== "CLOSED",
                    )}
                    onChange={dropDownChange}
                  />
                </Container>
              </Grid.Item>
            </Grid>
            <Grid
              cols="4"
              cols-sm="8"
              colsGap="30px"
              margin="6 0 0 0"
              margin-sm="8 0 0 0"
            >
              <Grid.Item colSpan="all" colSpan-sm="2-7">
                <DirectDebitLandingContent
                  dispatch={dispatch}
                  isOverdue={isOverdue}
                  cardToken={cardToken}
                />
              </Grid.Item>
            </Grid>
          </>
        );
      }}
    </Form>
  );
};
